import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORY_COLORS, STATUS_LABEL, STATUS_COLORS, formatSeconds, isHistoricalResult } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ImportReport } from "@/components/ImportReport";
import { toast } from "sonner";
import { Plus, Search, Upload, Download, Trash2, Pencil, X } from "lucide-react";

export default function Competitors() {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState("all"); // "all" | event id | "none"
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const [importReport, setImportReport] = useState(null);

  const load = useCallback(
    () => api.get("/competitors").then((r) => setRows(r.data || [])).catch(() => toast.error("Failed to load")),
    []
  );
  const loadEvents = useCallback(
    () => api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {}),
    []
  );
  useEffect(() => { load(); loadEvents(); }, [load, loadEvents]);

  const eventById = useMemo(
    () => Object.fromEntries(events.map((e) => [e.id, e])),
    [events]
  );

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    return rows.filter((r) => {
      const matchQ = !needle || r.full_name?.toLowerCase().includes(needle) || r.parent_name?.toLowerCase().includes(needle);
      const matchC = catFilter === "all" || r.assigned_category === catFilter || (catFilter === "unassigned" && !r.assigned_category);
      const matchE = eventFilter === "all" || (eventFilter === "none" ? !r.event_id : r.event_id === eventFilter);
      return matchQ && matchC && matchE;
    });
  }, [rows, q, catFilter, eventFilter]);

  const removeOne = async (id) => {
    if (!window.confirm("Delete this competitor?")) return;
    await api.delete(`/competitors/${id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = async () => {
    const url = `${api.defaults.baseURL}/competitors/export.csv`;
    const resp = await fetch(url, { credentials: "include" });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "competitors.csv";
    a.click();
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/competitors/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportReport(data);
      toast.success(`${data.created} created, ${data.updated || 0} updated, ${data.review?.length || 0} need review`);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Import failed. Check the CSV format and try again.");
    } finally {
      e.target.value = "";
    }
  };

  const competitionEvents = events.filter((e) => e.kind === "competition");
  const hasCompetitions = competitionEvents.length > 0;

  const tryAddCompetitor = () => {
    if (!hasCompetitions) {
      toast.error("Create a competition first before adding competitors.");
      return;
    }
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">Competitors</h1>
          <p className="text-[#7A7D8C] mt-1 text-sm">Full roster with timing solves, competition results, and assigned categories.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={onImport} data-testid="csv-import-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="csv-import-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]"><Upload className="w-4 h-4" /> Import CSV</button>
          <button onClick={exportCsv} data-testid="csv-export-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]"><Download className="w-4 h-4" /> Export</button>
          <button onClick={tryAddCompetitor} data-testid="add-competitor-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Plus className="w-4 h-4" /> Add competitor</button>
        </div>
      </div>

      <ImportReport report={importReport} prefix="roster-import" />
      {!hasCompetitions && (
        <div className="mb-6 rounded-2xl border-2 border-dashed border-[#FFBF69]/60 bg-[#FFBF69]/10 p-6 flex flex-wrap items-center gap-4" data-testid="no-events-banner">
          <div className="flex-1 min-w-[240px]">
            <div className="font-display font-black text-lg text-[#8A5A17]">Create a competition first</div>
            <p className="text-sm text-[#8A5A17]/80 mt-1">Every competitor must be attached to an event. Set one up before adding cubers.</p>
          </div>
          <Link to="/admin/events" data-testid="banner-create-event" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF9F1C] text-white text-sm font-bold shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><Plus className="w-4 h-4" /> Add a competition</Link>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7D8C]" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or parent…" className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-[#FDFFFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
        </div>
        <select data-testid="filter-event" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="all">All events</option>
          {events.filter((e) => e.kind === "competition").map((e) => <option key={e.id} value={e.id}>{`${e.title} · ${e.start_date}`}</option>)}
          <option value="none">— No event —</option>
        </select>
        <select data-testid="filter-category" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="all">All categories</option>
          {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          <option value="unassigned">Unassigned</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="competitors-table">
            <thead>
              <tr className="bg-[#FDFFFC] text-[#7A7D8C] text-xs uppercase tracking-wider">
                <Th>Name</Th><Th>Event</Th><Th>Status</Th><Th>Parent</Th><Th>Parent-selected</Th><Th>Timing Solve</Th><Th>Recommended</Th><Th>Assigned</Th><Th>Competition result</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-[#7A7D8C]">No competitors yet. Click <b>Add competitor</b> to start.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-[#FFBF69]/5" data-testid={`row-${r.id}`}>
                  <Td><div className="font-semibold">{r.full_name}</div><div className="text-xs text-[#7A7D8C]">{r.contact_info}</div></Td>
                  <Td><span className="text-xs">{eventById[r.event_id]?.title || <span className="text-[#7A7D8C]">—</span>}</span></Td>
                  <Td><span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${STATUS_COLORS[r.status || "pending"]}`}>{STATUS_LABEL[r.status || "pending"]}</span></Td>
                  <Td>{r.parent_name || "—"}<span className="block text-xs text-[#7A7D8C]" data-testid={`roster-experience-${r.id}`}>Previous competitions: {r.previous_competitions ?? "Not recorded"}</span></Td>
                  <Td><CatBadge value={r.parent_selected_category} /></Td>
                  <Td>{formatSeconds(r.prelim_average)}</Td>
                  <Td><CatBadge value={r.recommended_category} /></Td>
                  <Td><CatBadge value={r.assigned_category} /></Td>
                  <Td><span data-testid={`roster-result-${r.id}`}>{r.official_dnf ? <span className="text-[#E71D36] font-semibold">DNF</span> : formatSeconds(r.official_average)}</span>{isHistoricalResult(r) && <span className="block text-[11px] text-[#7A7D8C]" data-testid={`roster-format-${r.id}`}>Historical average</span>}</Td>
                  <Td>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`edit-${r.id}`} className="p-2 rounded-lg hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeOne(r.id)} data-testid={`delete-${r.id}`} className="p-2 rounded-lg hover:bg-red-50 text-[#E71D36]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <CompetitorForm
          initial={editing}
          events={events.filter((e) => e.kind === "competition")}
          defaultEventId={eventFilter !== "all" && eventFilter !== "none" ? eventFilter : ""}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 font-semibold">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

function CatBadge({ value }) {
  const { CATEGORY_LABEL } = useCategories();
  if (!value) return <span className="text-xs text-[#7A7D8C]">—</span>;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${CATEGORY_COLORS[value] || ""}`}>{CATEGORY_LABEL[value]}</span>;
}

function CompetitorForm({ initial, events = [], defaultEventId = "", onClose, onSaved }) {
  const { ALL_CATEGORIES, CATEGORY_LABEL } = useCategories();
  const [form, setForm] = useState({
    event_id: initial?.event_id || defaultEventId || "",
    full_name: initial?.full_name || "",
    parent_name: initial?.parent_name || "",
    contact_info: initial?.contact_info || "",
    parent_selected_category: initial?.parent_selected_category || "",
    assigned_category: initial?.assigned_category || "",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.event_id) delete payload.event_id;
      if (!payload.parent_selected_category) delete payload.parent_selected_category;
      if (!payload.assigned_category) delete payload.assigned_category;
      if (initial) {
        await api.patch(`/competitors/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        delete payload.assigned_category;
        await api.post("/competitors", payload);
        toast.success("Competitor added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
      <form onSubmit={save} className="bg-white rounded-3xl max-w-lg w-full p-8 relative my-8" data-testid="competitor-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        <h2 className="font-display font-black text-2xl">{initial ? "Edit competitor" : "Add competitor"}</h2>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Competition <span className="text-[#E71D36]">*</span></label>
            <select data-testid="form-event" value={form.event_id} required onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="">— choose a competition —</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title} · {ev.start_date}</option>)}
            </select>
          </div>
          <Field label="Full name" value={form.full_name} required readOnly={initial?.name_source === "child_profile"} onChange={(v) => setForm({ ...form, full_name: v })} tid="form-full-name" />
          {initial?.name_source === "child_profile" && <p className="text-xs text-[#7A7D8C]" data-testid="linked-name-note">This name is managed by the linked child profile. Original registration: {initial.original_full_name}</p>}
          <Field label="Parent / guardian" value={form.parent_name} onChange={(v) => setForm({ ...form, parent_name: v })} tid="form-parent" />
          <Field label="Contact info (email / phone)" value={form.contact_info} onChange={(v) => setForm({ ...form, contact_info: v })} tid="form-contact" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Parent-selected category</label>
            <select data-testid="form-parent-cat" value={form.parent_selected_category} onChange={(e) => setForm({ ...form, parent_selected_category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="">— none —</option>
              {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </select>
          </div>
          {initial && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Assigned category</label>
              <select data-testid="form-assigned-cat" value={form.assigned_category} onChange={(e) => setForm({ ...form, assigned_category: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
                <option value="">— none —</option>
                {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Notes</label>
            <textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} data-testid="form-save-btn" className="px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, readOnly = false, type = "text", tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} type={type} value={value} required={required} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
    </div>
  );
}
